import React, { useEffect, useRef, useState } from "react";
import {
  Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Tooltip, CircularProgress, Typography
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import ArchiveIcon from "@material-ui/icons/Archive";
import DeleteIcon from "@material-ui/icons/Delete";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import AddIcon from "@material-ui/icons/Add";
import FolderOpenIcon from "@material-ui/icons/FolderOpen";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";
import api from "../../services/api";
import useConfirm from "../../hooks/useConfirm";

const useStyles = makeStyles(theme => ({
  nameCell: { display: "flex", alignItems: "center", gap: theme.spacing(1), minWidth: 0 },
  dot: {
    width: 10, height: 10, borderRadius: "50%", flexShrink: 0,
    background: theme.palette.text.disabled,
  },
  code: {
    fontFamily: "'SF Mono', 'Roboto Mono', Menlo, monospace",
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  muted: { color: theme.palette.text.hint },
  date: { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  overdue: {
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    color: theme.palette.error.main,
    fontWeight: 600,
  },
}));

const STATUS = [
  { value: "active", label: "Ativo", tone: "info" },
  { value: "on_hold", label: "Pausado", tone: "warning" },
  { value: "done", label: "Concluído", tone: "success" },
  { value: "archived", label: "Arquivado", tone: "neutral" }
];

const Projects = () => {
  const classes = useStyles();
  const [confirm, confirmDialog] = useConfirm();
  const history = useHistory();
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState({ status: "", search: "" });
  const [dlg, setDlg] = useState({ open: false, editing: null, form: {} });
  const [users, setUsers] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [clientLoading, setClientLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const clientSearchTimer = useRef(null);

  const load = async () => {
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.search) params.search = filter.search;
      const { data } = await api.get("/projects", { params });
      setList(data || []);
    } catch (e) { toast.error("Erro ao carregar projetos"); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => {
    api.get("/users").then(r => setUsers(r.data?.users || [])).catch(() => {});
  }, []);

  // busca assíncrona de clientes (3+ letras)
  useEffect(() => {
    if (clientSearchTimer.current) clearTimeout(clientSearchTimer.current);
    const term = (clientSearch || "").trim();
    if (term.length < 3) { setClientLoading(false); return undefined; }
    setClientLoading(true);
    clientSearchTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/clients", { params: { searchParam: term } });
        const arr = data?.clients ?? data?.data ?? data;
        const list = Array.isArray(arr) ? arr : [];
        setClientOptions(prev => {
          if (selectedClient && !list.find(c => c.id === selectedClient.id)) return [selectedClient, ...list];
          return list;
        });
      } catch { setClientOptions(selectedClient ? [selectedClient] : []); }
      finally { setClientLoading(false); }
    }, 300);
    return () => clientSearchTimer.current && clearTimeout(clientSearchTimer.current);
  }, [clientSearch, selectedClient]);

  const clientLabel = (c) => {
    if (!c) return "";
    if (c.personType === "PJ" || c.companyName || c.tradeName) return c.tradeName || c.companyName || "";
    return [c.firstName, c.lastName].filter(Boolean).join(" ") || c.companyName || c.tradeName || c.email || `Cliente #${c.id}`;
  };

  const openNew = () => {
    setSelectedClient(null);
    setClientOptions([]);
    setClientSearch("");
    setDlg({ open: true, editing: null, form: { name: "", code: "", status: "active", color: "#2196F3" } });
  };
  const openEdit = (p) => {
    const c = p.client || null;
    setSelectedClient(c);
    setClientOptions(c ? [c] : []);
    setClientSearch("");
    setDlg({ open: true, editing: p, form: { ...p } });
  };
  const close = () => {
    setSelectedClient(null);
    setClientOptions([]);
    setClientSearch("");
    setDlg({ open: false, editing: null, form: {} });
  };

  const save = async () => {
    try {
      if (!dlg.form.name) return toast.warn("Informe o nome");
      if (dlg.editing) await api.put(`/projects/${dlg.editing.id}`, dlg.form);
      else await api.post("/projects", dlg.form);
      toast.success("Projeto salvo");
      close(); load();
    } catch (e) { toast.error("Erro ao salvar"); }
  };

  const archive = async (p) => {
    if (!(await confirm({ title: `Arquivar o projeto "${p.name}"?`, message: "O projeto sai da lista ativa, mas continua acessível pelo filtro de arquivados." }))) return;
    await api.post(`/projects/${p.id}/archive`);
    load();
  };

  const remove = async (p) => {
    if (!(await confirm({ title: `Excluir "${p.name}"?`, message: "Todas as tarefas do projeto são removidas junto. Não dá para desfazer." }))) return;
    await api.delete(`/projects/${p.id}`);
    load();
  };

  const columns = [
    {
      key: "name",
      label: "Nome",
      render: p => (
        <div className={classes.nameCell}>
          <span className={classes.dot} style={{ background: p.color || undefined }} />
          <Typography variant="subtitle2" noWrap>{p.name}</Typography>
        </div>
      ),
    },
    { key: "code", label: "Código", render: p => <code className={classes.code}>{p.code || "—"}</code> },
    {
      key: "client",
      label: "Cliente",
      render: p =>
        clientLabel(p.client) || <span className={classes.muted}>Projeto interno</span>,
    },
    { key: "owner", label: "Responsável", render: p => p.owner?.name || "—" },
    {
      key: "status",
      label: "Status",
      render: p => {
        const s = STATUS.find(x => x.value === p.status) || STATUS[0];
        return <StatusChip dot tone={s.tone} label={s.label} />;
      },
    },
    { key: "startDate", label: "Início", render: p => <span className={classes.date}>{p.startDate || "—"}</span> },
    {
      key: "dueDate",
      label: "Prazo",
      render: p => {
        if (!p.dueDate) return <span className={classes.date}>—</span>;
        // Prazo vencido em projeto ainda aberto é o dado que faz alguém agir:
        // marcar em vermelho evita ter que comparar data mentalmente linha a linha.
        const atrasado =
          p.status !== "done" &&
          p.status !== "archived" &&
          p.dueDate < new Date().toISOString().slice(0, 10);
        return (
          <span className={atrasado ? classes.overdue : classes.date}>
            {p.dueDate}
          </span>
        );
      },
    },
    {
      key: "actions",
      label: "Ações",
      type: "actions",
      render: p => (
        <>
          <Tooltip title="Abrir">
            <IconButton size="small" onClick={() => history.push(`/projects/${p.id}`)}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => openEdit(p)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {p.status !== "archived" && (
            <Tooltip title="Arquivar">
              <IconButton size="small" onClick={() => archive(p)}>
                <ArchiveIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Excluir">
            <IconButton size="small" onClick={() => remove(p)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <MainContainer>
      {confirmDialog}
      <PageHeader
        title="Projetos"
        count={list.length}
        subtitle="Entregas com prazo, responsável e cliente. As tarefas de cada projeto ficam na tela do projeto."
        actions={
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openNew}>
            Novo projeto
          </Button>
        }
        filters={
          <>
            <TextField label="Buscar" value={filter.search}
              onChange={e => setFilter({ ...filter, search: e.target.value })}
              onKeyDown={e => e.key === "Enter" && load()} />
            <TextField select label="Status" value={filter.status} style={{ minWidth: 160 }}
              onChange={e => setFilter({ ...filter, status: e.target.value })}>
              <MenuItem value="">Todos</MenuItem>
              {STATUS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </TextField>
            <Button onClick={load}>Aplicar</Button>
          </>
        }
      />

      <DataTable
        columns={columns}
        rows={list}
        onRowClick={p => history.push(`/projects/${p.id}`)}
        emptyIcon={<FolderOpenIcon />}
        emptyTitle="Nenhum projeto"
        emptyDescription="Projetos agrupam tarefas com prazo e responsável — úteis para entregas que passam de um atendimento avulso."
        emptyAction={
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openNew}>
            Novo projeto
          </Button>
        }
      />

      <Dialog open={dlg.open} onClose={close} maxWidth="sm" fullWidth>
        <DialogTitle>{dlg.editing ? "Editar projeto" : "Novo projeto"}</DialogTitle>
        <DialogContent>
          <TextField label="Nome" fullWidth margin="dense" value={dlg.form.name || ""}
            onChange={e => setDlg({ ...dlg, form: { ...dlg.form, name: e.target.value } })} />
          <TextField label="Código" fullWidth margin="dense" value={dlg.form.code || ""}
            onChange={e => setDlg({ ...dlg, form: { ...dlg.form, code: e.target.value } })} />
          <TextField label="Descrição" fullWidth margin="dense" multiline rows={3} value={dlg.form.description || ""}
            onChange={e => setDlg({ ...dlg, form: { ...dlg.form, description: e.target.value } })} />
          <Autocomplete
            value={selectedClient}
            options={clientOptions}
            getOptionLabel={(o) => clientLabel(o)}
            getOptionSelected={(o, v) => o && v && o.id === v.id}
            filterOptions={(x) => x}
            loading={clientLoading}
            noOptionsText={(clientSearch || "").trim().length < 3 ? "Digite ao menos 3 letras (vazio = projeto interno)" : "Nenhum cliente encontrado"}
            onInputChange={(_, val, reason) => {
              if (reason === "input") setClientSearch(val);
              if (reason === "clear") setClientSearch("");
            }}
            onChange={(_, val) => {
              setSelectedClient(val);
              setDlg(d => ({ ...d, form: { ...d.form, clientId: val ? val.id : null } }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Cliente"
                margin="dense"
                fullWidth
                placeholder="Digite 3+ letras para buscar (vazio = projeto interno)"
                helperText={selectedClient ? "" : "Deixe em branco para projeto interno"}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {clientLoading ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
          <TextField select label="Responsável" fullWidth margin="dense" value={dlg.form.ownerId || ""}
            onChange={e => setDlg({ ...dlg, form: { ...dlg.form, ownerId: e.target.value || null } })}>
            <MenuItem value="">—</MenuItem>
            {users.map(u => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
          </TextField>
          <TextField select label="Status" fullWidth margin="dense" value={dlg.form.status || "active"}
            onChange={e => setDlg({ ...dlg, form: { ...dlg.form, status: e.target.value } })}>
            {STATUS.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
          </TextField>
          <div style={{ display: "flex", gap: 8 }}>
            <TextField label="Início" type="date" InputLabelProps={{ shrink: true }} margin="dense" value={dlg.form.startDate || ""}
              onChange={e => setDlg({ ...dlg, form: { ...dlg.form, startDate: e.target.value } })} />
            <TextField label="Prazo" type="date" InputLabelProps={{ shrink: true }} margin="dense" value={dlg.form.dueDate || ""}
              onChange={e => setDlg({ ...dlg, form: { ...dlg.form, dueDate: e.target.value } })} />
            <TextField label="Cor" type="color" margin="dense" value={dlg.form.color || "#2196F3"}
              onChange={e => setDlg({ ...dlg, form: { ...dlg.form, color: e.target.value } })} />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={save}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Projects;
